import React, { useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';

import { snakeCaseToCamel } from 'app/shared/utils/string';
import { activeExperiments } from 'app/shared/utils/useExperimentFeatureFlags/config';
import { useFeatureFlags } from 'app/shared/utils/useFeatureFlags';
import { useFeatureFlagsForCurrentPath } from 'app/shared/utils/useFeatureFlagsForCurrentPath';

interface Props {
  children: React.ReactNode;
}

interface FeatureFlagsRetrievalInfo {
  featureFlagsEnabled: Record<string, boolean>;
  error?: ApolloError;
  loading?: boolean;
  called?: boolean;
  queryRequired?: boolean;
}

export interface FeatureFlagsContextValues extends FeatureFlagsRetrievalInfo {
  featureFlagsLoaded: string[];
  experimentsTracked: {
    [experimentName: string]: boolean;
  };
  setExperimentsTracked: React.Dispatch<
    React.SetStateAction<{
      [experimentName: string]: boolean;
    }>
  >;
}

export const FeatureFlagsContext =
  React.createContext<FeatureFlagsContextValues>({
    featureFlagsLoaded: [],
    featureFlagsEnabled: {},
    error: undefined,
    loading: undefined,
    called: undefined,
    queryRequired: undefined,
    experimentsTracked: {},
    setExperimentsTracked: () => {},
  });

// routesConfig flags are loaded as plain global Flipper checks with no flipperId.
// Two categories of flags must NOT be listed in routesConfig and should instead be
// loaded lazily via a hook in the component that needs them:
//
//   1. Experiment flags — need MD5 variant assignment via useExperimentFeatureFlags.
//      These are stripped automatically below even if accidentally added.
//
//   2. Actor-specific non-experiment flags (e.g. visa_summer_series, enabled for a
//      handful of specific Flipper actors) — need a flipperId for Flipper.enabled?
//      to return the correct result. Load these via useFeatureFlagsForFlipperId
//      directly in the component that needs them.
const activeExperimentFlagNames = new Set(
  activeExperiments.map((exp) => exp.experimentFlag)
);

const getContextValue = (
  nonExperimentFlagsToLoad: string[],
  {
    featureFlagsEnabled,
    error,
    loading,
    called,
    queryRequired,
  }: FeatureFlagsRetrievalInfo,
  experimentsTracked: FeatureFlagsContextValues['experimentsTracked'],
  setExperimentsTracked: FeatureFlagsContextValues['setExperimentsTracked']
) => ({
  featureFlagsLoaded: nonExperimentFlagsToLoad.map((flag) =>
    snakeCaseToCamel(flag)
  ),
  featureFlagsEnabled,
  error,
  loading,
  called,
  queryRequired,
  experimentsTracked,
  setExperimentsTracked,
});

const FeatureFlagsProvider = ({ children }: Props) => {
  const featureFlagsToLoad = useFeatureFlagsForCurrentPath();

  // Strip experiment flags in case they were accidentally added to routesConfig
  const nonExperimentFlagsToLoad = featureFlagsToLoad.filter(
    (name) => !activeExperimentFlagNames.has(name)
  );

  const featureFlagsRetrievalInfo = useFeatureFlags(nonExperimentFlagsToLoad);

  const [experimentsTracked, setExperimentsTracked] = useState<{
    [experimentName: string]: boolean;
  }>({});

  const contextValue = useMemo(
    () =>
      getContextValue(
        nonExperimentFlagsToLoad,
        featureFlagsRetrievalInfo,
        experimentsTracked,
        setExperimentsTracked
      ),
    [nonExperimentFlagsToLoad, featureFlagsRetrievalInfo, experimentsTracked]
  );

  return (
    <FeatureFlagsContext.Provider value={contextValue}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

export default FeatureFlagsProvider;
